import { useEffect, useState } from "react";
import { AdminHead } from "./AdminLayout";
import { api } from "../lib/api";
import { SkeletonRows } from "../components/Skeleton";

const GA4 = "G-X5PTWNXPXD";
const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];
const PAGE_NAMES = {
  "/": "Home",
  "/studio": "The Rooms",
  "/gear": "Gear",
  "/prev-work": "Prev. Work",
  "/contact": "Contact / Book",
};
const ROOM_NAMES = { balboa: "Balboa Recording Studio", bungalow: "Balboa Bungalow", either: "Not sure yet" };

const Stat = ({ label, value, sub, testid }) => (
  <div className="stat" data-testid={testid}>
    <p className="label">{label}</p>
    <p className="disp" style={{ fontSize: 40, marginTop: 6, lineHeight: 1 }}>{value}</p>
    {sub && <p className="mono" style={{ fontSize: 12, marginTop: 8, opacity: 0.7 }}>{sub}</p>}
  </div>
);

export default function AnalyticsAdmin() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/funnel?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div data-testid="admin-analytics">
      <AdminHead title="Analytics" sub="Your own visitor numbers, plus Google Analytics for the deep cuts." />

      <div className="studio-switch" style={{ marginBottom: 28 }} data-testid="funnel-range">
        {RANGES.map((r) => (
          <button
            key={r.days}
            className={`tab studio-tab${days === r.days ? " active" : ""}`}
            onClick={() => setDays(r.days)}
            data-testid={`funnel-range-${r.days}`}
          >
            Last {r.label}
          </button>
        ))}
      </div>

      {loading && <SkeletonRows rows={4} h={92} gap={16} testid="funnel-skeleton" />}

      {!loading && data && (
        <>
          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16 }}
            data-testid="funnel-stats"
          >
            <Stat
              label="Visitors"
              value={data.siteVisitors}
              sub={`${data.sitePageViews} page views`}
              testid="funnel-visitors"
            />
            <Stat
              label="Viewed Contact"
              value={data.contactVisitors}
              sub={`${data.contactViews} views of the booking page`}
              testid="funnel-contact-views"
            />
            <Stat
              label="Booking requests"
              value={data.submissions}
              sub="Form actually submitted"
              testid="funnel-submissions"
            />
            <Stat
              label="Contact → request"
              value={`${data.conversionRate}%`}
              sub={`${data.visitorToLeadRate}% of all visitors`}
              testid="funnel-rate"
            />
          </div>

          <h2 className="disp" style={{ fontSize: 24, marginTop: 48 }}>Most visited pages</h2>
          <div className="row-list" style={{ marginTop: 16 }} data-testid="funnel-pages">
            {data.pages.length ? (
              data.pages.map((p) => (
                <div className="tape-row mono" key={p.path}>
                  <span>{PAGE_NAMES[p.path] || p.path}</span>
                  <span>{p.visitors} visitors</span>
                  <span>{p.views} views</span>
                </div>
              ))
            ) : (
              <div className="empty">— no visits recorded yet —</div>
            )}
          </div>

          <h2 className="disp" style={{ fontSize: 24, marginTop: 48 }}>Requests by room</h2>
          <div className="row-list" style={{ marginTop: 16 }} data-testid="funnel-rooms">
            {Object.keys(data.rooms).length ? (
              Object.entries(data.rooms).map(([room, n]) => (
                <div className="tape-row mono" key={room}>
                  <span>{ROOM_NAMES[room] || room}</span>
                  <span>{n}</span>
                </div>
              ))
            ) : (
              <div className="empty">— no requests in this window —</div>
            )}
          </div>
        </>
      )}

      <div className="card" style={{ maxWidth: 560, marginTop: 48 }}>
        <p className="label">Google Analytics 4</p>
        <p className="disp" style={{ fontSize: 28, marginTop: 8 }} data-testid="ga4-id">{GA4}</p>
        <div className="misreg-rule" style={{ margin: "24px 0" }} />
        <a
          className="btn"
          href="https://analytics.google.com/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="ga4-open-dashboard"
        >
          Open Google Analytics Dashboard →
        </a>
        <p className="mono" style={{ fontSize: 13, marginTop: 20, opacity: 0.7 }}>
          Traffic sources, devices and locations live in Google Analytics. Submissions also fire a{" "}
          <strong>generate_lead</strong> event there — mark it as a Key Event in GA4 → Admin → Events to see it
          in the conversions column.
        </p>
      </div>
    </div>
  );
}
